'use client';
import { Dialog } from '@headlessui/react';
import { useEffect, useRef, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { SlArrowLeft, SlArrowRight } from 'react-icons/sl';

import cn from '@/lib/clsxm';

import Carousel, { CarouselItem } from '@/components/@base/carousel';
import Container from '@/components/@base/container';
import XImage from '@/components/@base/x-image';

import { GalleryModel } from '@/models/product.model';

type Props = {
  data?: GalleryModel[];
  isOpen: boolean;
  onClose: () => void;
};

export const Gallery = ({ data, isOpen, onClose }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({});
  const prevRef = useRef<HTMLDivElement | null>(null);
  const nextRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen && data && data.length > 0) {
      const imagesToPreload = data.slice(0, 3);
      imagesToPreload.forEach((image, index) => {
        const img = new Image();
        img.src = image.publicLink;
        img.onload = () => {
          setImagesLoaded((prev) => ({ ...prev, [index]: true }));
        };
      });
    }
  }, [isOpen, data]);

  if (!data || data.length === 0) return null;

  const handleSlideChange = (swiper: any) => {
    setCurrentSlide(swiper.activeIndex);

    const nextIndex = swiper.activeIndex + 1;
    if (data && nextIndex < data.length && !imagesLoaded[nextIndex]) {
      const img = new Image();
      img.src = data[nextIndex].publicLink;
      img.onload = () => {
        setImagesLoaded((prev) => ({ ...prev, [nextIndex]: true }));
      };
    }
  };

  const handleClose = () => {
    setCurrentSlide(0);
    onClose();
  };

  const handleImageLoad = (index: number) => {
    setImagesLoaded((prev) => ({ ...prev, [index]: true }));
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className='relative z-100'>
      {/* Backdrop */}
      <div className='fixed inset-0 bg-black/70' aria-hidden='true' onClick={handleClose} />
      {/* Full-screen container */}
      <div className='fixed inset-0 flex items-center justify-center'>
        <Dialog.Panel
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-prevent-close]')) return;
            handleClose();
          }}
          className='relative mb-5 flex h-full w-full items-center justify-center pt-5 pr-5 pl-6 md:pt-[50px] md:pr-[52px] md:pb-[52px] md:pl-[53px]'
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className='absolute top-8 left-7 z-40 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-white text-[#959595] transition-colors duration-300 hover:bg-white/80 hover:text-black/60 md:top-[50px] md:left-[53px] md:h-10 md:w-10'
            aria-label='close'
          >
            <AiOutlineClose size={15} />
          </button>

          {/* Carousel container */}
          <div className='flex h-full w-full items-center justify-center'>
            <Carousel
              slidesPerView={1}
              spaceBetween={0}
              autoplay={false}
              navigation={{ enabled: true, nextEl: nextRef.current, prevEl: prevRef.current }}
              onBeforeInit={(swiper: any) => {
                swiper.params.navigation.nextEl = nextRef.current;
                swiper.params.navigation.prevEl = prevRef.current;
              }}
              onInit={(swiper: any) => {
                swiper.navigation.update();
              }}
              onSlideChange={handleSlideChange}
              className='flex h-full w-full items-center justify-center [&_.swiper-button-disabled]:hidden [&_.swiper-slide]:flex [&_.swiper-slide]:items-center [&_.swiper-slide]:justify-center'
            >
              {data.map((image, index) => (
                <CarouselItem key={image.id || index} className=''>
                  <Container className='mx-auto flex h-full w-fit items-center justify-center'>
                    <Container className='relative' data-prevent-close>
                      <XImage
                        src={image.publicLink}
                        alt={image.title || image.name}
                        width={1200}
                        height={800}
                        priority={index === 0}
                        loading={index < 3 ? 'eager' : 'lazy'}
                        onLoad={() => handleImageLoad(index)}
                        className='h-auto max-h-[80vh] w-auto max-w-[80vw] object-contain'
                      />
                      <div
                        className={cn(
                          imagesLoaded[currentSlide] ? 'opacity-100' : 'opacity-0',
                          'absolute -bottom-9 left-4 -translate-x-1/2 transform rounded-full px-4 py-2 text-[15px] font-bold whitespace-nowrap text-[#B1B4BD] transition-opacity duration-200',
                        )}
                      >
                        {currentSlide + 1} از {data.length}
                      </div>
                    </Container>
                  </Container>
                </CarouselItem>
              ))}
              <Container
                ref={nextRef}
                className='custom-swiper-button-next absolute top-1/2 left-0 z-10 -translate-y-1/2 transform cursor-pointer'
                center
                data-prevent-close
              >
                <Container className='flex h-10 w-10 items-center justify-center rounded-full bg-white/70 md:h-[53px] md:w-[53px]'>
                  <Container className='flex h-8 w-8 items-center justify-center rounded-full bg-white pr-1 shadow-[0px_5px_15px_0px_#00000026] transition duration-300 hover:bg-white/60 md:h-[41px] md:w-[41px] md:pr-0'>
                    <SlArrowLeft />
                  </Container>
                </Container>
              </Container>
              <Container
                ref={prevRef}
                className='custom-swiper-button-prev absolute top-1/2 right-0 z-10 -translate-y-1/2 transform cursor-pointer'
                center
                data-prevent-close
              >
                <Container className='flex h-10 w-10 items-center justify-center rounded-full bg-white/70 md:h-[53px] md:w-[53px]'>
                  <Container className='flex h-8 w-8 items-center justify-center rounded-full bg-white pl-1 shadow-[0px_5px_15px_0px_#00000026] transition duration-300 hover:bg-white/60 md:h-[41px] md:w-[41px] md:pl-0'>
                    <SlArrowRight />
                  </Container>
                </Container>
              </Container>
            </Carousel>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
